import React, { useContext } from 'react';
import { Button, Typography, Container, Grid, Card, CardContent, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';

const SnapSortInfo = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleCTAClick = () => {
    if (user) {
      navigate('/classify-save');
    } else {
      navigate('/login');
    }
  };

  // Define your color palette here (matching Home component)
  const colors = {
    primary: "#550FCC",
    secondary: "#8338ec",
    background: "#f0f7ff",
    text: "#2b2d42",
    accent: "#ff006e",
    featureBackground: "#006064", // Deep teal for feature cards
  };

  const features = [
    {
      icon: "📸",
      title: "Easy Upload & Classification",
      description: "Quickly upload and automatically classify your images with AI-powered accuracy."
    },
    {
      icon: "🏷️",
      title: "Smart Tagging",
      description: "Automatically generate relevant tags for your images to improve searchability."
    },
    {
      icon: "🔍",
      title: "Visual Search",
      description: "Find similar images in your collection using advanced image recognition technology."
    },
    {
      icon: "💻",
      title: "Web Image Search",
      description: "Extend your search to the web to find related images and content."
    },
  ];

  const applications = [
    {
      title: "Digital Marketing",
      description: "Efficiently organize and retrieve brand assets and campaign materials."
    },
    {
      title: "Real Estate",
      description: "Streamline property image management and enhance listing searches."
    },
    {
      title: "Media & Entertainment",
      description: "Organize vast libraries of visual content for quick access and use."
    },
    {
      title: "Education",
      description: "Manage and categorize educational resources and visual aids effectively."
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: "100%",
        paddingLeft: "0",
        paddingRight: "0",
        marginTop: "4rem"
      }}
    >
      {/* Hero Section with Features */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#E0F7FA",
          paddingTop: "4rem",
          paddingBottom: "4rem",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{ fontWeight: "bold", marginBottom: "1rem", color: colors.primary }}>
                SnapSort: Intelligent Image Organization
              </Typography>
              <Typography variant="h5" sx={{ marginBottom: "2rem", color: colors.text }}>
                Organize, search, generate and leverage your image collection like never before.
              </Typography>
              <Button
                variant="contained"
                onClick={handleCTAClick}
                sx={{
                  padding: "12px 24px",
                  fontSize: "1.2rem",
                  borderRadius: "8px",
                  backgroundColor: colors.accent,
                  "&:hover": {
                    backgroundColor: colors.primary,
                  },
                }}
              >
                {user ? 'Start Organizing' : 'Get Started'}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card sx={{ 
                      height: '100%', 
                      backgroundColor: colors.featureBackground, 
                      color: '#FFFFFF',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                      }
                    }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ marginBottom: "0.5rem" }}>
                          {feature.icon}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2">
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Applications Section */}
      <Container maxWidth="lg" sx={{ marginTop: "4rem" }}>
        <Typography variant="h3" sx={{ fontWeight: "bold", color: colors.primary, marginBottom: "2rem", textAlign: "center" }}>
          SnapSort in Action
        </Typography>
        <Grid container spacing={4}>
          {applications.map((app, index) => (
            <Grid item xs={12} md={3} key={index}>
              <Card sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: '#F0EFEA',
                padding: "2rem",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-5px)",
                },
              }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: "1rem", color: colors.text }}>
                    {app.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    {app.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call-to-Action Section */}
      <Box sx={{
        width: "100%",
        backgroundColor: colors.primary,
        marginTop: "4rem",
        padding: "4rem 0",
        color: "white",
        textAlign: "center",
      }}>
        <Typography variant="h3" sx={{ fontWeight: "bold", marginBottom: "1rem" }}>
          Ready to Revolutionize Your Image Management?
        </Typography>
        <Typography variant="h6" sx={{ marginBottom: "2rem" }}>
          Start organizing and searching your images with AI-powered precision.
        </Typography>
        <Button
          variant="contained"
          onClick={handleCTAClick}
          sx={{
            padding: "12px 24px",
            fontSize: "1.2rem",
            borderRadius: "8px",
            backgroundColor: "white",
            color: colors.secondary,
            "&:hover": {
              backgroundColor: colors.accent,
              color: "white",
            },
          }}
        >
          {user ? 'Start Organizing' : 'Get Started'}
        </Button>
      </Box>
    </Container>
  );
};

export default SnapSortInfo;